import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CrudService } from '../shared/grid/crud.service';
import { JurisdictionRiskConfig } from '../models/jurisdictionRiskConfig.model';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface ParsedLikelihoodLevel {
    QualThresholds?: [number, number];
}

@Injectable({
  providedIn: 'root'
})

export class JurisdictionRiskConfigService extends CrudService<JurisdictionRiskConfig> {
  constructor(http: HttpClient) {
    super('JurisdictionRiskConfig', http);
  }

  getByJurisdictionId(jurisdictionId: string): Observable<JurisdictionRiskConfig | null> {
    return this.http.get<JurisdictionRiskConfig | null>
      (this.getApiUrl(`${this.controllerName}/GetByJurisdictionId?jurisdictionId=${jurisdictionId}`));
  }

  getByJurisdictionIds(ids: string[]): Observable<JurisdictionRiskConfig[]> {
    return this.http.post<JurisdictionRiskConfig[]>(
      this.getApiUrl(`${this.controllerName}/GetByJurisdictionIds`), ids
    ).pipe(
      catchError(() => {
        const filterBy = JSON.stringify({ JurisdictionId: ids.join(',') });
        return this.http.get<JurisdictionRiskConfig[]>(
          this.getApiUrl(`${this.controllerName}/GetAllEntities?filterBy=${encodeURIComponent(filterBy)}`)
        );
      })
    );
  }

  /**
   * Validate that QualThreshold ranges in ExposureLikelihoodLevels cover the full range of possible qualitative products (1 to N*M).
   * @param exposureLikelihoodLevels JSON string of exposure likelihood levels
   * @param maxProduct Maximum possible qualitative product (exceedance dimensions * frequency dimensions)
   * @returns { valid: boolean, errors: string[] } Validation result with errors if any
   */
  validateQualThresholdRanges(exposureLikelihoodLevels: string, maxProduct: number): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    try {
      const parsed = JSON.parse(exposureLikelihoodLevels);
      const levels = Object.entries(parsed as Record<string, ParsedLikelihoodLevel>)
        .filter(([key]) => key !== 'ConsequenceRatings')
        .map(([_, value]) => ({
          minQual: value.QualThresholds?.[0] ?? 0,
          maxQual: value.QualThresholds?.[1] ?? 0
        }));

      if (levels.length === 0) {
        errors.push('No exposure likelihood levels defined.');
        return { valid: false, errors };
      }

      // Validate all thresholds are integers
      for (let i = 0; i < levels.length; i++) {
        if (!Number.isInteger(levels[i].minQual)) {
          errors.push(`Level ${i}: minimum threshold (${levels[i].minQual}) must be an integer.`);
        }
        if (!Number.isInteger(levels[i].maxQual)) {
          errors.push(`Level ${i}: maximum threshold (${levels[i].maxQual}) must be an integer.`);
        }
      }

      if (errors.length > 0) {
        return { valid: false, errors };
      }

      // Sort by minQual to check continuity
      levels.sort((a, b) => a.minQual - b.minQual);

      // Check that minimum starts at 1
      if (levels[0].minQual !== 1) {
        errors.push(`Qualitative thresholds must start at 1, but start at ${levels[0].minQual}.`);
      }

      // Check that maximum reaches maxProduct
      if (levels[levels.length - 1].maxQual !== maxProduct) {
        errors.push(`Qualitative thresholds must end at ${maxProduct}, but end at ${levels[levels.length - 1].maxQual}.`);
      }

      // Check for gaps and overlaps between levels
      for (let i = 0; i < levels.length - 1; i++) {
        const currentMax = levels[i].maxQual;
        const nextMin = levels[i + 1].minQual;
        if (currentMax + 1 < nextMin) {
          errors.push(`Gap between level ${i} (max ${currentMax}) and level ${i + 1} (min ${nextMin}). Thresholds must be contiguous.`);
        } else if (currentMax + 1 > nextMin) {
          errors.push(`Overlap between level ${i} (max ${currentMax}) and level ${i + 1} (min ${nextMin}). Thresholds must be contiguous.`);
        }
      }

      // Check for inverted ranges (min > max within a single level)
      for (let i = 0; i < levels.length; i++) {
        if (levels[i].minQual > levels[i].maxQual) {
          errors.push(`Level ${i}: minimum threshold (${levels[i].minQual}) cannot be greater than maximum (${levels[i].maxQual}).`);
        }
      }

      return { valid: errors.length === 0, errors };
    } catch (e) {
      errors.push(`Failed to parse exposure likelihood levels: ${e instanceof Error ? e.message : String(e)}`);
      return { valid: false, errors };
    }
  }
}
